import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { groupApi, ListGroupsParams, ListGroupMembersParams } from '../services/userApi';
import { GroupCreate, GroupUpdate } from '../types';

export const GROUPS_QUERY_KEY = 'groups';
export const GROUP_MEMBERS_QUERY_KEY = 'group-members';

export function useGroups(params?: ListGroupsParams) {
  return useQuery({
    queryKey: [GROUPS_QUERY_KEY, params],
    queryFn: () => groupApi.list(params),
  });
}

export function useGroup(id: string) {
  return useQuery({
    queryKey: [GROUPS_QUERY_KEY, id],
    queryFn: () => groupApi.get(id),
    enabled: !!id,
  });
}

export function useCreateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: GroupCreate) => groupApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GROUPS_QUERY_KEY] });
    },
  });
}

export function useUpdateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: GroupUpdate }) =>
      groupApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GROUPS_QUERY_KEY] });
    },
  });
}

export function useDeleteGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => groupApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GROUPS_QUERY_KEY] });
    },
  });
}

export function useGroupMembers(groupId: string, params?: ListGroupMembersParams) {
  return useQuery({
    queryKey: [GROUP_MEMBERS_QUERY_KEY, groupId, params],
    queryFn: () => groupApi.getMembers(groupId, params),
    enabled: !!groupId,
  });
}

export function useAddGroupMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      groupApi.addMember(groupId, userId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [GROUP_MEMBERS_QUERY_KEY, variables.groupId] });
    },
  });
}

export function useRemoveGroupMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      groupApi.removeMember(groupId, userId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [GROUP_MEMBERS_QUERY_KEY, variables.groupId] });
    },
  });
}
